import { useAccountModal, useConnectModal } from '@rainbow-me/rainbowkit'
import { useState, useEffect, lazy } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  useAccount,
  useDisconnect,
  useReadContracts,
  useSignMessage
} from 'wagmi'
import { useTranslation } from 'react-i18next'

import {
  DEFAULT_CONTAINER_STATE,
  EXCHANGE_PROVIDER_PROCESS_STEP,
  STAKING_PROCESS_STEP
} from '../constants/containerStates'
import { CUSTOM_BRAND_ID } from '../constants/pointsExchange'
import { EXCHANGE_ACCOUNT_ACTION } from '../constants/exchangeProvider'
import {
  connectExchangeProvider,
  updateExchangeProvider
} from '../store/actions/exchange'
import {
  selectAccountProfile,
  selectExchangeProviderProfile,
  selectStakingProfile
} from '../store/selectors/common'
import { getWalletAddressSelector } from '../store/selectors/wallet'
import {
  disconnectMetamaskWallet,
  isWalletConnected,
  stakingContractConfig,
  tokenContractConfig
} from '../util/staking/staking.helpers'
import { toLower } from '../util/string.helpers'
import FailedTransaction from '../components/popups/wallet/FailedTransaction'
import { STAKING_PROFILE_DISPATCH } from '../store/reducers/staking/staking'
import {
  changeStakingInfoState,
  changeStakingState
} from '../store/actions/staking'
import { formatAmount } from '../util/number.helpers'
import { changeMainState } from '../store/actions/main'
import { MAIN_PROFILE_DISPATCH } from '../store/reducers/main/main'
const WalletCannotChange = lazy(
  () => import('../components/popups/wallet/WalletCannotChange')
)
const ConnectWalletPopup = lazy(
  () => import('../components/popups/wallet/ConnectWallet')
)

const stakingConfig = stakingContractConfig()

const useWalletConnection = () => {
  const { t } = useTranslation()

  const walletAddressMembership = useSelector(getWalletAddressSelector)
  const accountProfile = useSelector(selectAccountProfile)
  const stakingProfile = useSelector(selectStakingProfile)
  const exchangeProviderProfile = useSelector(selectExchangeProviderProfile)

  const connectedWallet = walletAddressMembership.membershipNumber
  const { openConnectModal, connectModalOpen } = useConnectModal()
  const { openAccountModal } = useAccountModal()
  const { address, isConnected } = useAccount()
  const { disconnect } = useDisconnect()

  const [popupState, setPopupState] = useState({
    id: DEFAULT_CONTAINER_STATE.NONE
  })

  const [walletMembership, setWalletMembership] = useState(null)
  const dispatch = useDispatch()

  const connected = isWalletConnected({
    address,
    isConnected
  })

  useEffect(() => {
    if (
      exchangeProviderProfile?.currentBrandId === CUSTOM_BRAND_ID.ETH_ADDRESS &&
      exchangeProviderProfile?.accountSaveState ===
        EXCHANGE_PROVIDER_PROCESS_STEP.WRONG_ACCOUNT_NUMBER
    ) {
      handleWalletError(
        t('membership.wallet-connection-failed-desc'),
        t('membership.wallet-connection-failed')
      )
    }
  }, [exchangeProviderProfile?.accountSaveState])

  const {
    signMessage,
    error: signMsgErr,
    data: signMsgData
    // isPending: signMsgPending
  } = useSignMessage()

  const handleWalletError = (message, title) => {
    setPopupState({
      id: DEFAULT_CONTAINER_STATE.SOMETHING_WENT_WRONG,
      data: { message, title }
    })
    disconnect()
    disconnectMetamaskWallet()
  }

  useEffect(() => {
    if (signMsgErr && signMsgErr.shortMessage) {
      handleWalletError(signMsgErr.shortMessage)
    }
  }, [signMsgErr])

  useEffect(() => {
    if (signMsgData) {
      dispatchAction(STAKING_PROFILE_DISPATCH.SDK_PROCESSING, false)
      // Connect/Update Wallet membership
      if (walletMembership === EXCHANGE_ACCOUNT_ACTION.CHANGE) {
        dispatch(
          updateExchangeProvider(
            walletAddressMembership.id,
            CUSTOM_BRAND_ID.ETH_ADDRESS,
            address,
            signMsgData
          )
        )
      } else if (walletMembership === EXCHANGE_ACCOUNT_ACTION.CONNECT) {
        dispatch(
          connectExchangeProvider(
            CUSTOM_BRAND_ID.ETH_ADDRESS,
            address,
            signMsgData
          )
        )
      }
    }
  }, [signMsgData])

  useEffect(() => {
    if (stakingProfile.sdkProcessing && connected) {
      executeSig()
    }
  }, [connected, stakingProfile.sdkProcessing])

  useEffect(() => {
    if (!connectModalOpen) {
      dispatchAction(STAKING_PROFILE_DISPATCH.SDK_PROCESSING, false)
    }
  }, [connectModalOpen])

  useEffect(() => {
    if (connectedWallet) {
      setWalletMembership(null)
      dispatchAction(STAKING_PROFILE_DISPATCH.SDK_PROCESSING, false)
    }
  }, [connectedWallet])

  const executeSig = () => {
    const authId = accountProfile.authId
    if (
      walletAddressMembership &&
      walletAddressMembership.id &&
      toLower(connectedWallet) !== toLower(address)
    ) {
      signMessage({ message: authId })
      setWalletMembership(EXCHANGE_ACCOUNT_ACTION.CHANGE)
    } else {
      if (!connectedWallet) {
        signMessage({ message: authId })
        setWalletMembership(EXCHANGE_ACCOUNT_ACTION.CONNECT)
      }
    }
  }

  const stakingContractResponse = useReadContracts({
    contracts: [
      ...(connectedWallet
        ? [
            {
              ...stakingConfig,
              functionName: 'getStakeInfo',
              args: [connectedWallet]
            }
          ]
        : [])
    ]
  })

  useEffect(() => {
    if (connectedWallet) {
      dispatchAction(STAKING_PROFILE_DISPATCH.STAKING_INFO_PROCESSING, true)
      if (stakingContractResponse?.data?.length) {
        const result = stakingContractResponse.data[0]?.result
        if (result && result.length) {
          const [stakedQBX, endTime] = result
          dispatch(
            changeStakingInfoState(formatAmount(stakedQBX), parseInt(endTime))
          )
        } else {
          dispatchAction(
            STAKING_PROFILE_DISPATCH.STAKING_INFO_PROCESSING,
            false
          )
        }
      } else {
        dispatchAction(STAKING_PROFILE_DISPATCH.STAKING_INFO_PROCESSING, false)
      }
    }
  }, [stakingContractResponse.data])

  // Fetching QBX Balance
  const qbxContractResponse = useReadContracts({
    contracts: [
      ...(connectedWallet
        ? [
            {
              ...tokenContractConfig(),
              functionName: 'balanceOf',
              args: [connectedWallet],
              watch: true
            }
          ]
        : [])
    ]
  })

  useEffect(() => {
    if (connectedWallet) {
      // dispatchAction(STAKING_PROFILE_DISPATCH.LOADING, true)
      dispatchMainAction(MAIN_PROFILE_DISPATCH.LOADING, true)
      if (qbxContractResponse?.data?.length) {
        const balance = formatAmount(qbxContractResponse?.data[0]?.result)
        if (balance) {
          dispatchMainAction(MAIN_PROFILE_DISPATCH.LOADING, false)
          dispatchAction(STAKING_PROFILE_DISPATCH.AVAILABLE_QBX, balance)
        } else {
          dispatchAction(STAKING_PROFILE_DISPATCH.AVAILABLE_QBX, 0)
          // dispatchAction(STAKING_PROFILE_DISPATCH.LOADING, false)
          dispatchMainAction(MAIN_PROFILE_DISPATCH.LOADING, false)
        }
      } else {
        // dispatchAction(STAKING_PROFILE_DISPATCH.LOADING, false)
        dispatchAction(STAKING_PROFILE_DISPATCH.AVAILABLE_QBX, 0)
        dispatchMainAction(MAIN_PROFILE_DISPATCH.LOADING, false)
      }
    }
  }, [qbxContractResponse.data])

  const dispatchAction = (property, value) => {
    dispatch(changeStakingState(property, value))
  }

  const dispatchMainAction = (property, value) => {
    dispatch(changeMainState(property, value))
  }

  const handleNoPopup = () => {
    setPopupState({ data: null, id: DEFAULT_CONTAINER_STATE.NONE })
  }

  const handlePopupSubmit = () => {
    switch (popupState.id) {
      case STAKING_PROCESS_STEP.CONNECT_WALLET:
        handleNoPopup()
        dispatchAction(STAKING_PROFILE_DISPATCH.SDK_PROCESSING, true)
        openConnectModal()
        break

      default:
        handleNoPopup()
        break
    }
  }

  const handlePopupCancel = () => {
    switch (popupState.id) {
      default:
        handleNoPopup()
        break
    }
  }

  const renderPopup = () => {
    switch (popupState.id) {
      case DEFAULT_CONTAINER_STATE.SOMETHING_WENT_WRONG:
        return (
          <FailedTransaction
            onSubmit={handlePopupSubmit}
            onCancel={handlePopupCancel}
            {...popupState.data}
          />
        )

      case STAKING_PROCESS_STEP.CANNOT_CHANGE_WALLET:
        return (
          <WalletCannotChange
            onSubmit={handlePopupSubmit}
            onCancel={handlePopupCancel}
            t={t}
          />
        )

      case STAKING_PROCESS_STEP.CONNECT_WALLET:
        return (
          <ConnectWalletPopup
            onSubmit={handlePopupSubmit}
            onCancel={handlePopupCancel}
            authId={accountProfile.authId}
            t={t}
          />
        )

      default:
        break
    }
  }

  const handleWalletClick = () => {
    if (stakingProfile.stakedQBX > 0) {
      setPopupState({
        id: STAKING_PROCESS_STEP.CANNOT_CHANGE_WALLET
      })
    } else {
      if (connected) {
        openAccountModal()
      } else {
        setPopupState({ id: STAKING_PROCESS_STEP.CONNECT_WALLET })
      }
    }
  }

  return {
    openConnectModal,
    onWalletClick: handleWalletClick,
    renderWalletPopup: renderPopup,
    stakingContractResponse,
    qbxContractResponse
  }
}

export { useWalletConnection }
